import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Table, TableBody, TableCell,
  TableRow, Divider, Chip,
} from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';

const COMMAND_DECK_DESCRIPTION = 'Shortcut help is a non-diagnostic command deck; all controls stay off image pixels.';

const SECTIONS = [
  {
    title: 'Window / Level',
    shortcuts: [
      { keys: ['1'], description: 'Soft Tissue  W400 / L40' },
      { keys: ['2'], description: 'Lung  W1500 / L-600' },
      { keys: ['3'], description: 'Vessel / CTA  W600 / L200' },
      { keys: ['4'], description: 'Bone  W2000 / L400' },
      { keys: ['5'], description: 'Brain  W80 / L40' },
      { keys: ['6'], description: 'Stroke  W40 / L40' },
      { keys: ['7'], description: 'Abdomen  W350 / L50' },
      { keys: ['0'], description: 'Auto W/L (percentile-based)' },
      { keys: ['Right-click'], description: 'W/L preset context menu' },
    ],
  },
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['Scroll'], description: 'Scroll through slices' },
      { keys: ['W'], description: 'Window / Level tool' },
      { keys: ['S'], description: 'Scroll tool' },
      { keys: ['M'], description: 'Measurement tool' },
      { keys: ['R'], description: 'ROI tool' },
      { keys: ['A'], description: 'Arrow annotation tool' },
      { keys: ['T'], description: 'Text annotation tool' },
      { keys: ['V'], description: 'Select / edit annotations' },
      { keys: ['P'], description: 'Pen annotation tool' },
      { keys: ['B'], description: 'Rectangle annotation tool' },
      { keys: ['E'], description: 'Ellipse annotation tool' },
      { keys: ['Drag (W/L mode)'], description: 'Adjust window / level' },
      { keys: ['Click×2 (Measure)'], description: 'Draw ruler measurement' },
      { keys: ['Click×2 (Arrow)'], description: 'Place arrow annotation' },
      { keys: ['Click (Text)'], description: 'Place text label, Enter to save' },
      { keys: ['Drag (ROI)'], description: 'Draw circular ROI with HU stats' },
      { keys: ['Select tool', 'Click/Drag'], description: 'Select and move frontend annotations' },
      { keys: ['Select tool', 'Drag blue handles'], description: 'Edit arrow endpoints, rectangle/ellipse edges, angle/Cobb points, text/level positions' },
      { keys: ['Delete'], description: 'Delete selected annotation' },
      { keys: ['Escape'], description: 'Cancel pending annotation / clear selection' },
      { keys: ['Drag (Pen)'], description: 'Draw freehand pen annotation' },
      { keys: ['Drag (Rect)'], description: 'Draw rectangle annotation' },
      { keys: ['Drag (Ellipse)'], description: 'Draw ellipse annotation separate from ROI' },
      { keys: ['Click×3 (Angle)'], description: '3-point angle measurement' },
      { keys: ['Click×4 (Cobb)'], description: 'Cobb/spine angle skeleton' },
      { keys: ['Click (Level)'], description: 'Place vertebral level marker' },
      { keys: ['Color/W/LABELS'], description: 'Style controls affect the selected annotation; otherwise they set defaults for new annotations' },
    ],
  },
  {
    title: 'General',
    shortcuts: [
      { keys: ['?'], description: 'Show / hide this help panel' },
    ],
  },
];

const KeyChip = ({ label }) => (
  <Chip label={label} size="small" className="commandDeckKeycap" sx={{
    bgcolor: 'linear-gradient(180deg, rgba(110,239,255,0.16), rgba(21,43,59,0.92))', color: '#d7fbff',
    border: '1px solid rgba(110,239,255,0.34)', borderRadius: 1.25,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), 0 0 14px rgba(0,229,255,0.10)',
    fontSize: 10, fontFamily: 'monospace', fontWeight: 800, height: 20, mr: 0.5,
    letterSpacing: '0.04em', minWidth: 24,
  }} />
);

export default function KeyboardShortcutsHelp({ open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-describedby="keyboard-shortcuts-command-deck-description"
      PaperProps={{
        className: 'keyboardShortcutCommandDeck',
        sx: {
          bgcolor: '#061018',
          border: '1px solid rgba(110,239,255,0.28)',
          borderRadius: 2.5,
          background: 'linear-gradient(145deg, rgba(5,14,22,0.98), rgba(10,26,38,0.96) 48%, rgba(2,8,14,0.99))',
          boxShadow: '0 28px 80px rgba(0,0,0,0.62), 0 0 42px rgba(0,229,255,0.10), inset 0 1px 0 rgba(255,255,255,0.08)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, color: '#d7fbff', fontSize: 13, letterSpacing: 1.8, py: 1.5, borderBottom: '1px solid rgba(110,239,255,0.16)' }}>
        <Box sx={{ width: 30, height: 30, borderRadius: 2, display: 'grid', placeItems: 'center', color: '#6eefff', bgcolor: 'rgba(110,239,255,0.10)', border: '1px solid rgba(110,239,255,0.24)', boxShadow: '0 0 18px rgba(0,229,255,0.12)' }}>
          <KeyboardIcon fontSize="small" />
        </Box>
        <Box>
          <Typography component="div" sx={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.8, fontFamily: 'monospace', lineHeight: 1.15 }}>
            KAIROS COMMAND DECK
          </Typography>
          <Typography id="keyboard-shortcuts-command-deck-description" sx={{ color: 'rgba(215,251,255,0.58)', fontSize: 9, letterSpacing: 0.8, fontFamily: 'monospace', mt: 0.35 }}>
            {COMMAND_DECK_DESCRIPTION}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, background: 'radial-gradient(circle at 18% 0%, rgba(110,239,255,0.08), transparent 34%)' }}>
        {SECTIONS.map((section, si) => (
          <Box key={si}>
            <Box sx={{ px: 3, py: 0.9, bgcolor: 'rgba(2,8,14,0.72)', borderTop: si === 0 ? 0 : '1px solid rgba(110,239,255,0.10)' }}>
              <Typography sx={{ color: '#6eefff', fontSize: 9, letterSpacing: 2.2, fontWeight: 800, fontFamily: 'monospace' }}>
                {section.title.toUpperCase()}
              </Typography>
            </Box>
            <Divider sx={{ borderColor: 'rgba(110,239,255,0.12)' }} />
            <Table size="small">
              <TableBody>
                {section.shortcuts.map((s, i) => (
                  <TableRow key={i} sx={{ '&:hover': { bgcolor: 'rgba(110,239,255,0.055)' }, transition: 'background-color 120ms ease', '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}>
                    <TableCell sx={{ borderBottom: '1px solid rgba(110,239,255,0.08)', width: 190, py: 0.65, px: 3 }}>
                      {s.keys.map(k => <KeyChip key={k} label={k} />)}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(110,239,255,0.08)', color: 'rgba(231,247,255,0.86)', fontSize: 12, py: 0.65, pr: 3 }}>
                      {s.description}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 1.5, pt: 1, borderTop: '1px solid rgba(110,239,255,0.12)', bgcolor: 'rgba(2,8,14,0.62)' }}>
        <Button onClick={onClose} size="small" sx={{ color: 'rgba(215,251,255,0.66)', border: '1px solid rgba(110,239,255,0.18)', borderRadius: 1.5, px: 1.5, '&:hover': { color: '#6eefff', borderColor: 'rgba(110,239,255,0.34)', bgcolor: 'rgba(110,239,255,0.06)' }, fontFamily: 'monospace', fontSize: 11, letterSpacing: 1 }}>
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  );
}
